import React from 'react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * PermissionGate Component
 * 
 * Controls visibility/behavior of UI elements based on permissions.
 * 
 * Usage Examples:
 * 
 * 1. Hide element if no permission:
 *    <PermissionGate permission={PERMISSIONS.RBAC_USER_MANAGEMENT_CREATE}>
 *      <button>Create User</button>
 *    </PermissionGate>
 * 
 * 2. Disable element if no permission:
 *    <PermissionGate permission={PERMISSIONS.RBAC_USER_MANAGEMENT_EDIT} mode="disable">
 *      <button>Edit User</button>
 *    </PermissionGate>
 * 
 * 3. Require ANY permission from array:
 *    <PermissionGate permissions={[PERMISSIONS.RBAC_USER_MANAGEMENT_EDIT, PERMISSIONS.RBAC_USER_MANAGEMENT_DELETE]}>
 *      <button>Manage User</button>
 *    </PermissionGate>
 * 
 * 4. Require ALL permissions from array:
 *    <PermissionGate permissions={[PERMISSIONS.RBAC_USER_MANAGEMENT_VIEW, PERMISSIONS.RBAC_USER_MANAGEMENT_EDIT]} requireAll>
 *      <button>Edit User</button>
 *    </PermissionGate>
 * 
 * 5. Show fallback when no permission:
 *    <PermissionGate permission={PERMISSIONS.RBAC_VIEW} fallback={<div>Access Denied</div>}>
 *      <AdminPanel />
 *    </PermissionGate>
 * 
 * Props:
 * - permission: Single permission string
 * - permissions: Array of permissions
 * - requireAll: If true with permissions array, user needs ALL (default: false - needs ANY)
 * - mode: 'hide' (default) or 'disable'
 * - fallback: Component to show when permission denied (only works with mode='hide')
 * - disabledClassName: CSS classes to apply when disabled
 * - children: Component to control
 */
const PermissionGate = ({
  permission,
  permissions,
  requireAll = false,
  mode = 'hide',
  fallback = null,
  disabledClassName = 'opacity-50 cursor-not-allowed',
  children,
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isSuperAdmin } = useAuth();

  // Super Admin bypasses all checks
  if (isSuperAdmin) {
    return <>{children}</>;
  }

  // Determine if user has required permission(s)
  let isAuthorized = true;

  if (permission) {
    // Single permission check
    isAuthorized = hasPermission(permission);
  } else if (permissions && permissions.length > 0) {
    // Multiple permissions check
    if (requireAll) {
      isAuthorized = hasAllPermissions(permissions);
    } else {
      isAuthorized = hasAnyPermission(permissions);
    }
  } else {
    // No permission specified, allow access
    isAuthorized = true;
  }

  // If not authorized
  if (!isAuthorized) {
    if (mode === 'hide') {
      // Hide element completely, optionally show fallback
      return fallback;
    }

    if (mode === 'disable') {
      // Disable element (works for buttons, inputs, etc.)
      return React.cloneElement(React.Children.only(children), {
        disabled: true,
        className: `${children.props.className || ''} ${disabledClassName}`.trim(),
        onClick: (e) => {
          e.preventDefault();
          e.stopPropagation();
        },
        style: {
          ...children.props.style,
          pointerEvents: 'none',
        },
      });
    }
  }

  // User is authorized, render children normally
  return <>{children}</>;
};

/**
 * Convenience component for checking role-based access
 * 
 * Usage:
 * <RoleGate roles={[ROLES.SUPER_ADMIN]}>
 *   <AdminPanel />
 * </RoleGate>
 */
export const RoleGate = ({
  role,
  roles,
  mode = 'hide',
  fallback = null,
  disabledClassName = 'opacity-50 cursor-not-allowed',
  children,
}) => {
  const { hasRole, hasAnyRole, isSuperAdmin } = useAuth();

  // Super Admin bypasses all checks
  if (isSuperAdmin) {
    return <>{children}</>;
  }

  // Check roles
  let isAuthorized = true;

  if (role) {
    isAuthorized = hasRole(role);
  } else if (roles && roles.length > 0) {
    isAuthorized = hasAnyRole(roles);
  } else {
    isAuthorized = true;
  }

  // If not authorized
  if (!isAuthorized) {
    if (mode === 'hide') {
      return fallback;
    }

    if (mode === 'disable') {
      return React.cloneElement(React.Children.only(children), {
        disabled: true,
        className: `${children.props.className || ''} ${disabledClassName}`.trim(),
        onClick: (e) => {
          e.preventDefault();
          e.stopPropagation();
        },
        style: {
          ...children.props.style,
          pointerEvents: 'none',
        },
      });
    }
  }

  return <>{children}</>;
};

export default PermissionGate;
